<script lang="ts">
  // (c) Cognisoft Aps, 2022, for Svelte 5: 2026
  // spell noun from picture
  // model without stressed vowel
  // on-screen keyboard

  import { flip } from "svelte/animate";
  import { crossfade } from "svelte/transition";
  import { onDestroy } from "svelte";

  import { Pages, DaDK, Path, Extension, State } from "../enums";
  import { HelpLevel } from "../classes";
  import {
    afterCorrectWaitSecs,
    alfaKeyCaps,
    qwertyKeyCaps,
  } from "../constants";

  import { Icons } from "../utilities/icons";
  import { playSoundWithAudioTag } from "../utilities/playSound";
  import { playCorrectRandomWithAudioTag } from "../utilities/playCorrectRandom";
  import { getTotalNumberOfTasks } from "../utilities/getTotalNumberOfTasks";
  import { setCompletedBackgroundColor } from "../utilities/setCompletedBackgroundColor";
  import { logger } from "../utilities/logger";

  import { studentUserData } from "../stores/user-store.svelte";
  import { route } from "../stores/route.svelte";
  import {
    currentSentenceNumberForSteps,
    targetsForSteps,
  } from "../stores/targets-step567.svelte"; // last picture shown in step 4
  import { targetVerbAndObjects } from "../globals/targets";
  import { progress } from "../stores/progress.svelte";

  import { helpCount } from "../stores/helpCount.svelte";
  import { result } from "../stores/result.svelte";
  import { tasksCompleted } from "../stores/tasksCompleted.svelte";

  import ExitModal from "../modals/ExitModal.svelte";

  import RoundButton from "../components/RoundButton.svelte";
  import ProgressBar from "../components/ProgressBar.svelte";
  import PauseMessage from "../components/PauseMessage.svelte";

  // PROPS --------------------------------------------------

  // one audiotag in App.svelte is used on all pages in order to have
  //   sound played even before mouse click

  let { audioTag }: { audioTag: HTMLAudioElement } = $props();

  // TYPES --------------------------------------------------

  // has to be object to make key for svelte animation
  //  because letter may be repeated, and repeated strings not allowed
  type Letter = {
    letter: string;
  };

  type Letters = Array<Letter>;

  // CONSTANTS ---------------------------------------------

  const [send, receive] = crossfade({});

  const vowels = ["a", "e", "i", "o", "u", "y", "æ", "ø", "å"];

  const vowelTargetPostion =
    targetVerbAndObjects.value[
      targetsForSteps.value[currentSentenceNumberForSteps.value]
    ].vowelTargetPosition.indexOf("_");

  const numberOfHelpLevels = 4;

  // STATE ------------------------------------------------

  let bipAudioTag: HTMLAudioElement;

  let keyCaps: Array<string> = $state([]);

  let currentState: State = $state(State.PresentTask); // currentState for task solution for each item/picture
  let answerWord: string = $state(""); // word to be copied, starts empty

  let indexOfLetterToRemove: number = $state(0); // index of letter to remove from model word

  let modelLetters: Letters = $state([]); // model string made into array of objects
  let receivedLetters: Letters = $state([]); // only one received but still has to be string because of Svelte animate

  let hideVowel = $state(false);
  let hideRemoved = $state(false);

  let repeatButtonTimer: ReturnType<typeof setTimeout>;
  let helpTimer: ReturnType<typeof setTimeout>;

  // help both after a set time, requested and after error
  let helpLevel = $state(new HelpLevel(numberOfHelpLevels));

  let allowRepetition = $state(false);
  let requestedHelpPaused = $state(false); // // help not active for some seconds after activted last time

  let helpLetters: Array<string> = $state([]);

  // obs exception to help hierarky for stressed vowel, extra helpstep 3 highlighted vowels
  let hasShown3HelpVowels = $state(false);

  // for results:
  let gotHelpForItem = false;
  let hasErrorForItem = false;

  // FOR RESEARCH ONLY
  // // save results for each response, push to array in results.store
  // let presentTime: Date;
  // let numberOfTimedHelp = 0;
  // let numberOfRequestedHelp = 0;
  // let numberOfRequestedRepetitions = 0;
  // let attempt = 1;
  // let responseDetails: Array<ResponseDetail> = [];

  // keep track of pause time
  let pauseStartTime: number;
  let pauseEndTime: number;

  // EFFECTS --------------------------------------------------

  $effect(() => {
    logger("Step6 currentState:", currentState);
  });
  $effect(() => {
    logger("Step6 helpLevel:", helpLevel.level);
  });

  // FUNCTIONS --------------------------------------------

  const playErrorSound = () => {
    bipAudioTag.src = Path.Snd + "bip" + Extension.Mp3;
    bipAudioTag.load();
    bipAudioTag.play();
  };

  const countHelp = () => {
    switch (currentSentenceNumberForSteps.value) {
      case 0:
        helpCount.value.spelling.item61++;
        break;
      case 1:
        helpCount.value.spelling.item62++;
        break;
      case 2:
        helpCount.value.spelling.item63++;
        break;
      case 3:
        helpCount.value.spelling.item64++;
        break;
    }
  };

  // adjust help count by number of letters in word to get a mean
  const adjustHelpCount = () => {
    switch (currentSentenceNumberForSteps.value) {
      case 0:
        helpCount.value.spelling.item61 =
          helpCount.value.spelling.item61 /
          targetVerbAndObjects.value[
            targetsForSteps.value[currentSentenceNumberForSteps.value]
          ].object.length;
        break;
      case 1:
        helpCount.value.spelling.item62 =
          helpCount.value.spelling.item62 /
          targetVerbAndObjects.value[
            targetsForSteps.value[currentSentenceNumberForSteps.value]
          ].object.length;
        break;
      case 2:
        helpCount.value.spelling.item63 =
          helpCount.value.spelling.item63 /
          targetVerbAndObjects.value[
            targetsForSteps.value[currentSentenceNumberForSteps.value]
          ].object.length;
        break;
      case 3:
        helpCount.value.spelling.item64 =
          helpCount.value.spelling.item64 /
          targetVerbAndObjects.value[
            targetsForSteps.value[currentSentenceNumberForSteps.value]
          ].object.length;
        break;
    }
  };

  // show repeat button a defined time after showin task

  const startRepeatButtonTimer = () => {
    if (currentState === State.WaitAfterEnd) return;
    allowRepetition = false;
    repeatButtonTimer = setTimeout(() => {
      allowRepetition = true;
    }, studentUserData.value.settings.repeatDelaySecs * 1000);
  };

  // showing help according to 2 level hierarkies (timed and requested help) ---

  const enableHelpButtonDelayed = () => {
    setTimeout(() => {
      requestedHelpPaused = false;
    }, 1000);
  };

  const startHelpTimer = () => {
    if (currentState === State.WaitAfterEnd) return;
    currentState = State.WaitForResponse;
    requestedHelpPaused = false;
    helpTimer = setTimeout(() => {
      // numberOfTimedHelp++;  // FOR RESEARCH ONLY
      giveHelp(false);
    }, studentUserData.value.settings.helpDelaySecs * 1000);
  };

  const startHelpTimerAndWaitForResponse = (afterError: boolean) => {
    if (currentState === State.WaitAfterEnd) return;
    currentState = State.WaitForResponse;
    startHelpTimer();
    // FOR RESEARCH ONLY:
    // if (afterError) {
    //   initializeResultVariables();
    // }
  };

  // showing help according to eval hierarchy

  // if error for stressed vowels 3 vowels should be highlighted
  const make3HelpVowels = () => {
    hasShown3HelpVowels = true;
    const firstInserted =
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].stressedVowel;
    helpLetters.push(firstInserted);
    let secondInserted: string;
    do {
      secondInserted = vowels[Math.floor(Math.random() * vowels.length)];
    } while (secondInserted === firstInserted);
    helpLetters.push(secondInserted);
    let thirdInserted: string;
    do {
      thirdInserted = vowels[Math.floor(Math.random() * vowels.length)];
    } while (
      thirdInserted === firstInserted ||
      thirdInserted == secondInserted
    );
    helpLetters.push(thirdInserted);
  };

  // sound both repeated with speaker button and at 2 first help stages
  const repeatObjectHelp = (afterError: boolean): void => {
    if (currentState === State.WaitAfterEnd) return;
    helpLevel.setNextLevel();
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object,
      startHelpTimerAndWaitForResponse.bind(null, afterError),
    );
  };

  // obs exception to help hierarchy for stressed vowel, extra help step 3 highlighted vowels
  const showCorrectLetterHelp = (afterError: boolean) => {
    const letterPosition = answerWord.length;
    if (letterPosition === vowelTargetPostion && !hasShown3HelpVowels) {
      make3HelpVowels();
    } else {
      helpLevel.setNextLevel();
      helpLetters = [];
      helpLetters.push(
        targetVerbAndObjects.value[
          targetsForSteps.value[currentSentenceNumberForSteps.value]
        ].object.charAt(letterPosition),
      );
    }
    helpLetters = helpLetters;
    currentState = State.WaitForResponse;
    enableHelpButtonDelayed();
    startHelpTimerAndWaitForResponse(afterError);
  };

  const showCorrectAnswerHelp = (afterError: boolean) => {
    const letterPosition = answerWord.length - 1;
    const insertLetter = targetVerbAndObjects.value[
      targetsForSteps.value[currentSentenceNumberForSteps.value]
    ].object.charAt(letterPosition + 1);
    playSoundWithAudioTag(audioTag, insertLetter, null);
    answerWord = answerWord + insertLetter;
    helpLetters = [];
    if (
      answerWord ===
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object
    ) {
      clearTimeout(helpTimer);
      afterCorrect();
    } else {
      helpLevel.setNextLevel();
      currentState = State.WaitForResponse;
      enableHelpButtonDelayed();
      clearTimeout(helpTimer);
      startHelpTimerAndWaitForResponse(afterError);
    }
  };

  // give requested help after clicking help button or after timed help according to help level
  const giveHelp = (afterError: boolean) => {
    if (
      currentState !== State.WaitForResponse &&
      currentState !== State.Error
    ) {
      return;
    }
    currentState = State.GivingHelp;
    logger("Step6 giveHelp, helpLevel:", helpLevel.level);
    countHelp();
    gotHelpForItem = true;
    requestedHelpPaused = true;
    clearTimeout(helpTimer);
    switch (helpLevel.level) {
      case 1:
      case 2:
        repeatObjectHelp(afterError);
        break;
      case 3:
        showCorrectLetterHelp(afterError);
        break;
      case 4:
        showCorrectAnswerHelp(afterError);
        break;
      default:
        console.error(
          "step 6: error in giveHelp() switch, helpLevel: ",
          helpLevel.level,
        );
    }
  };

  const requestedHelp = (): void => {
    logger("Step6 requestedHelp");
    if (currentState !== State.WaitForResponse) {
      return;
    }
    clearTimeout(helpTimer);
    currentState = State.RepeatingSound;
    countHelp();
    // numberOfRequestedHelp++;  // FOR RESEARCH ONLY
    requestedHelpPaused = true;
    playSoundWithAudioTag(audioTag, DaDK.Step6Help, startHelpTimer);
  };

  const repeatSound = () => {
    if (currentState !== State.WaitForResponse) {
      return;
    }
    currentState = State.RepeatingSound;
    countHelp();
    // numberOfRequestedRepetitions++;  // FOR RESEARCH ONLY
    clearTimeout(helpTimer);
    requestedHelpPaused = true;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object,
      startHelpTimer,
    );
  };

  // start ---

  const startAndSayIntro = (): void => {
    currentState = State.PlayIntro;
    playSoundWithAudioTag(audioTag, DaDK.Step6Intro, startAndSayWord);
  };

  const startAndSayWord = (): void => {
    hideVowel = true;
    receivedLetters.push(modelLetters[indexOfLetterToRemove]);
    modelLetters.splice(indexOfLetterToRemove, 1, { letter: "_" });
    playSoundWithAudioTag(audioTag, DaDK.Write, startAndSayWord2);
  };

  const startAndSayWord2 = (): void => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object,
      WaitForResponse,
    );
  };

  const WaitForResponse = (): void => {
    currentState = State.WaitForResponse;
    hideRemoved = true;
    startRepeatButtonTimer();
    startHelpTimer();
    // initializeResultVariables();  // FOR RESEARCH ONLY
  };

  // correct ---

  const afterCorrect = (): void => {
    setCompletedBackgroundColor(true);
    tasksCompleted.value = tasksCompleted.value + 1;
    // result.value.responseDetails.push(...responseDetails);
    result.value.lastStepCompleted = 6;
    if (!gotHelpForItem && !hasErrorForItem) {
      result.value.spellingCorrectFirstTry =
        result.value.spellingCorrectFirstTry + 1;
    }
    // adjust help count by number of letters in word
    adjustHelpCount();
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object,
      gotoNextTask,
    );
  };

  const gotoNextTask = () => {
    currentState = State.WaitAfterEnd;
    setTimeout(() => {
      route.current = Pages.Step7;
    }, afterCorrectWaitSecs);
  };

  // error ---
  const afterError = () => {
    giveHelp(true);
  };

  // button click events  ---

  const keyClicked = (letter: string): void => {
    const letterPosition = answerWord.length;
    if (
      letter ===
      targetVerbAndObjects.value[
        targetsForSteps.value[currentSentenceNumberForSteps.value]
      ].object.charAt(letterPosition)
    ) {
      // saveResultVariablesWithCorrect(true);  // FOR RESEARCH ONLY
      helpLetters = [];
      answerWord = answerWord + letter;
      helpLevel.reset();
      // obs exception to help hierarky for stressed vowel, extra helpstep 3 highlighted vowels:
      hasShown3HelpVowels = false;
      clearTimeout(helpTimer);
      clearTimeout(repeatButtonTimer);
      if (
        answerWord.length ===
        targetVerbAndObjects.value[
          targetsForSteps.value[currentSentenceNumberForSteps.value]
        ].object.length
      ) {
        // attempt = 1;  // FOR RESEARCH ONLY
        currentState = State.Correct;
        progress.value += 1;
        progress;
        allowRepetition = false;
        playCorrectRandomWithAudioTag(audioTag, afterCorrect);
      } else {
        WaitForResponse();
      }
    } else {
      // saveResultVariablesWithCorrect(false);  // FOR RESEARCH ONLY
      // attempt++;  // FOR RESEARCH ONLY
      currentState = State.Error;
      hasErrorForItem = true;
      if (helpLevel.level === numberOfHelpLevels) {
        afterError();
      } else {
        requestedHelpPaused = true;
        // test if first entry is the stressed vowel and give special message
        if (
          answerWord.length === 0 &&
          letter ===
            targetVerbAndObjects.value[
              targetsForSteps.value[currentSentenceNumberForSteps.value]
            ].stressedVowel
        ) {
          // stop general help timer
          clearTimeout(helpTimer);
          // special message about stressed vowel
          playSoundWithAudioTag(
            audioTag,
            DaDK.Step6FoundVowel,
            afterStressedVowelError,
          );
        } else {
          playSoundWithAudioTag(audioTag, DaDK.TryAgain, afterError);
        }
      }
    }
  };

  // special case, stressed vowel as error in first position i word
  // called after playing error message has finished
  // it does not go into general error hierarchy
  const afterStressedVowelError = () => {
    requestedHelpPaused = false;
    startHelpTimerAndWaitForResponse(true);
  };

  // keyboard input ---

  const handleKeydown = (event: KeyboardEvent): void => {
    if (currentState !== State.WaitForResponse) {
      playErrorSound();
      return;
    }
    let letter = event.key.toLocaleLowerCase();
    if (keyCaps.includes(letter)) {
      keyClicked(letter);
    } else if (event.key === "?" && !requestedHelpPaused) {
      requestedHelp();
    }
  };

  // pause --------

  const pauseClicked = (): void => {
    if (currentState === State.Paused) {
      pauseEndTime = Date.now();
      result.value.pauseElapsed =
        result.value.pauseElapsed + Math.abs(pauseEndTime - pauseStartTime);
      allowRepetition = true;
      startHelpTimer();
    } else {
      pauseStartTime = Date.now();
      // stopSoundWithAudioTag(audioTag);
      clearTimeout(repeatButtonTimer);
      clearTimeout(helpTimer);
      currentState = State.Paused;
    }
  };

  // exit task prematurely after stop button clicked

  // stop button: show yes-no modal
  const exitButtonClicked = () => {
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    currentState = State.ExitModalShown;
  };

  // exit task (modal event "yes")
  const exitTask = () => {
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    result.value.wasInterrupted = true;
    route.current = Pages.EndResults;
  };

  // dismiss exit modal (event "no")
  const dismissExitModal = () => {
    allowRepetition = true;
    startHelpTimer();
  };

  // FOR RESEARCH ONLY --------------------------------------------

  // save result details for a single item, used for cvs results
  // *** used for research disabled in current production

  // const saveResultVariablesWithCorrect = (correct: boolean) => {
  // // for research - disabled in current production
  // responseDetails.push({
  //   step: 6,
  //   attempt: attempt,
  //   presentTime: presentTime,
  //   responseTime: new Date(),
  //   targetItem:
  //     targetVerbAndObjects.value[targetsForSteps.value[currentSentenceNumberForSteps.value]]
  //       .object,
  //   correct: correct,
  //   numberOfTimedHelp: numberOfTimedHelp,
  //   numberOfRequestedHelp: numberOfRequestedHelp,
  //   numberOfRequestedRepetitions: numberOfRequestedRepetitions,
  // });
  // };

  // LIFE CYCLE ---------------------------------------------------

  onDestroy(() => {
    clearTimeout(helpTimer);
    clearTimeout(repeatButtonTimer);
  });

  // INITIALIZE --------------------------------------------

  // FOR RESEARCH ONLY
  // const initializeResultVariables = () => {
  //   presentTime = new Date();
  //   numberOfTimedHelp = 0;
  //   numberOfRequestedHelp = 0;
  //   numberOfRequestedRepetitions = 0;
  // };

  setCompletedBackgroundColor(false);

  if (studentUserData.value.settings.qwertyKeyboard) {
    keyCaps = [...qwertyKeyCaps];
  } else {
    keyCaps = [...alfaKeyCaps];
  }

  indexOfLetterToRemove =
    targetVerbAndObjects.value[
      targetsForSteps.value[currentSentenceNumberForSteps.value]
    ].vowelTargetPosition.indexOf("_");

  // make array of objects for model word because of svelte animate
  for (let char of targetVerbAndObjects.value[
    targetsForSteps.value[currentSentenceNumberForSteps.value]
  ].object) {
    modelLetters.push({ letter: char });
  }

  startAndSayIntro();
</script>

<!-- HTML ===================================================== -->

<svelte:window onkeydown={handleKeydown} />

<audio bind:this={bipAudioTag}></audio>

<!-- data for Playwright tests -->
<div data-testid="state" data-state={currentState} style="display: none;"></div>
<div
  data-testid="word"
  data-word={targetVerbAndObjects.value[
    targetsForSteps.value[currentSentenceNumberForSteps.value]
  ].object}
  style="display: none;"
></div>

<main data-page="step6">
  <!-- top panel with help and repeat buttons -->
  <div class="top-panel">
    <div>
      <span class="stop-button">
        <RoundButton
          disabled={currentState !== State.WaitForResponse}
          onclick={exitButtonClicked}
          icon={Icons.TrafficLightRed}
        />
      </span>
      <RoundButton
        disabled={currentState !== State.WaitForResponse &&
          currentState !== State.Paused}
        onclick={pauseClicked}
        icon={currentState === State.Paused ? Icons.Play : Icons.Pause}
      />
    </div>
    <div>
      <RoundButton
        disabled={currentState !== State.WaitForResponse || !allowRepetition}
        onclick={repeatSound}
        icon={Icons.VolumeRegular}
      />
      <span class="help-button">
        <RoundButton
          disabled={currentState !== State.WaitForResponse ||
            requestedHelpPaused}
          onclick={requestedHelp}
          icon={Icons.QuestionSolid}
        />
      </span>
    </div>
  </div>
  <!-- image showing target -->
  <div class="img-removed-area">
    <div
      class="image"
      style="background-image: url('{encodeURI(
        Path.ImgBg +
          targetVerbAndObjects.value[
            targetsForSteps.value[currentSentenceNumberForSteps.value]
          ].image +
          Extension.ImgBg,
      )}'); 
        background-image: image-set('{encodeURI(
        Path.ImgBg +
          targetVerbAndObjects.value[
            targetsForSteps.value[currentSentenceNumberForSteps.value]
          ].image +
          Extension.WebpBg,
      )}'); 
        background-size: cover;"
    >
      <picture>
        <source
          type="image/webp"
          srcset={encodeURI(
            Path.Img +
              targetVerbAndObjects.value[
                targetsForSteps.value[currentSentenceNumberForSteps.value]
              ].image +
              Extension.Webp,
          )}
          draggable={false}
        />
        <img
          draggable={false}
          src={encodeURI(
            Path.Img +
              targetVerbAndObjects.value[
                targetsForSteps.value[currentSentenceNumberForSteps.value]
              ].image +
              Extension.Png,
          )}
          alt=""
        />
      </picture>
    </div>
    <div class="removed-letter">
      {#each receivedLetters as letter (letter)}
        <div
          class="model-letter"
          class:hide-removed={hideRemoved}
          animate:flip
          in:receive|global={{ key: letter }}
        >
          {letter.letter}
        </div>
      {/each}
    </div>
  </div>
  <!-- word model to copy from without stressed vowel -->
  <div class="model-div">
    {#each modelLetters as letter, index (letter)}
      <div
        class="model-letter"
        animate:flip
        out:send|global={{ key: letter }}
        class:hidden-letter={hideVowel && index === indexOfLetterToRemove}
      >
        {letter.letter}
      </div>
    {/each}
  </div>
  <!-- copied word -->
  <div class="copy-div">
    {answerWord}
  </div>

  <!-- on-screen keyboard -->
  <div
    class="keyboard-div"
    class:qwerty-keys={studentUserData.value.settings.qwertyKeyboard}
  >
    {#each keyCaps as keyCap}
      <button
        class:key-help={helpLetters.includes(keyCap) &&
          currentState === State.WaitForResponse}
        class="key-cap key-cap-{keyCap}"
        disabled={currentState !== State.WaitForResponse}
        onclick={() => keyClicked(keyCap)}
      >
        {keyCap}
      </button>
    {/each}
  </div>
  <div class="progress">
    <ProgressBar
      max={getTotalNumberOfTasks(studentUserData.value.currentLevel)}
      position={progress.value}
    />
  </div>

  <!-- MODALS -------------------------------------- -->
  {#if currentState === State.Paused}
    <PauseMessage pauseMessageClicked={pauseClicked} />
  {/if}

  {#if currentState === State.ExitModalShown}
    <ExitModal yes={exitTask} no={dismissExitModal} />
  {/if}
</main>

<!-- CSS ====================================================== -->
<style>
  main {
    margin-left: auto;
    margin-right: auto;
    width: 120vh;
    height: calc(var(--vh, 1vh) * 100);
    display: grid;
    grid-template-columns: 100%;
    grid-template-rows: 14% 30% 12% 10% 31% 2%;
    grid-template-areas:
      "top"
      "img-removed"
      "word-model"
      "word-copy"
      "keyboard"
      "prog";
    gap: 1%;
    padding: 3%;
    padding-top: 2%;
    padding-left: 2%;
  }
  @media screen and (max-aspect-ratio: 6/5) {
    main {
      width: 100vw;
    }
  }
  .top-panel {
    grid-area: top;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .image {
    background: white;
    border: solid 1px black;
    grid-area: img;
    width: 40%;
  }
  @media screen and (max-aspect-ratio: 6/5) {
    .image {
      width: 50%;
    }
  }
  @media screen and (max-aspect-ratio: 5/5) {
    .image {
      width: 60%;
    }
  }
  @media screen and (max-aspect-ratio: 4/5) {
    .image {
      width: 70%;
    }
  }
  @media screen and (max-aspect-ratio: 3/5) {
    .image {
      width: 80%;
    }
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: bottom;
  }
  .model-div {
    grid-area: word-model;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
  .progress {
    grid-area: prog;
    width: 100%;
  }
  .copy-div {
    grid-area: word-copy;
    background: white;
    border: solid 1px black;
    font-size: 7.5vmin;
    text-transform: uppercase;
    letter-spacing: 2vmin;
    padding-left: 2vmin;
    margin-bottom: 1vmin;
    line-height: 1.05;
  }
  .keyboard-div {
    grid-area: keyboard;
    display: grid;
    width: 100%;
    grid-template-columns: repeat(10, 1fr);
    grid-template-rows: repeat(3, 1fr);
  }
  .qwerty-keys {
    grid-template-columns: repeat(11, 1fr);
  }
  .model-letter {
    width: 10vmin;
    height: 10vmin;
    font-size: 8vmin;
    text-transform: uppercase;
    border: solid 1px black;
    background: white;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 0.5vmin;
    user-select: none;
    transition:
      transform 0.5s,
      background-color 0.5s,
      color 0.1s;
    transform-style: preserve-3d;
  }
  .img-removed-area {
    grid-area: img-removed;
    display: flex;
  }
  .removed-letter {
    align-self: center;
    margin-left: 2vw;
  }
  .hide-removed {
    transform: rotateY(180deg);
    color: lightGray;
    background-color: lightGray;
  }
  .hidden-letter {
    visibility: hidden;
  }
  .key-cap {
    /* width: 9vw; */
    width: 94%;
    height: calc(var(--vh, 1vh) * 9);
    font-size: 7vmin;
    padding: 0;
    text-transform: uppercase;
    border: solid 0.45vmin var(--button-border-color);
    border-radius: 1vmin;
    background: white;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    color: black;
    user-select: none;
  }
  .key-cap[disabled] {
    color: gray;
    border-color: gray;
    cursor: auto;
  }
  @media (hover) {
    .key-cap:not([disabled]):hover {
      background-color: var(--key-hover-background-color);
      border-color: var(--key-hover-border-color);
    }
  }
  .key-help {
    background: var(--key-help-background-color);
  }
</style>
